import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { clearingHousePropertiesType, clearingHouseType } from '../../types';
import ReadonlyField from '../fields/ReadonlyField';
import ReadonlyList from '../fields/ReadonlyList';
import ExpandedListTab from '../ExpandedListTab';
import './_styles_/ClearingHouseDetailTab.css';
import Privileges from '../../utils/Privileges';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import useExtendedState from '../../hooks/useExtendedState';
import { CLEARING_HOUSE_NAME_MAX_LETTERS, nameValidator } from '../../validators/clearingHouses';
import { Privileged } from '../../hocs/withPrivileges';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import { ALL_CLEARING_HOUSES_QUERY_NAME, DELETE_CLEARING_HOUSE, UPDATE_CLEARING_HOUSE } from '../../graphql/clearingHouses';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import PrivilegedSelectField from '../fields/privileged/PrivilegedSelectField';
import DefaultCashLimitsTable from './DefaultCashLimitsTable';
import InfoPanel from '../InfoPanel';
import DeleteButton from '../buttons/DeleteButton';
import { transformClearingHouseBlockers, transformClearingHouseModificationBlockers } from '../blockers/ClearingHouseBlockers';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';
import useSingleValidator from '../../hooks/useSingleValidator';

ClearingHouseDetailTab.propTypes = {
    clearingHouse: clearingHouseType.isRequired,
    clearingHouseProps: clearingHousePropertiesType.isRequired,
    onDetailDelete: PropTypes.func.isRequired,
};

function ClearingHouseDetailTab(props) {
    const {
        clearingHouse,
        clearingHouseProps,
        onDetailDelete,
    } = props;
    const {
        availableCurrencies,
        availableTimeZones,
        minCashLimit,
        maxCashLimit,
    } = clearingHouseProps;
    const { t } = useTranslation();
    const [resetComponentsAfterModification, setResetComponentsAfterModification] = useState(false);
    const nameState = useExtendedState(clearingHouse.name);
    const timeZoneState = useExtendedState(clearingHouse.timeZone.ID);
    const defaultCashLimitsState = useExtendedState(availableCurrencies.map((availableCurrency) => {
        const defaultCashLimit = {
            currencyName: availableCurrency.name,
            currencyCode: availableCurrency.code,
            cashLimit: 0.00,
            isValid: true,
        };
        const existingCashLimit = clearingHouse.cashLimits.find((cashLimit) => cashLimit.currency.code === availableCurrency.code);
        if (existingCashLimit) {
            defaultCashLimit.cashLimit = existingCashLimit.cashLimit;
        }
        return defaultCashLimit;
    }));
    const onDefaultCashLimitChange = (currencyCode, cashLimit, isValidX) => {
        setResetComponentsAfterModification(false);
        const newDefaultCashLimits = defaultCashLimitsState.value.map((defaultCashLimit) => {
            let result = defaultCashLimit;
            if (defaultCashLimit.currencyCode === currencyCode) {
                result = {
                    currencyName: defaultCashLimit.currencyName,
                    currencyCode,
                    cashLimit,
                    isValid: isValidX,
                };
            }
            return result;
        });
        defaultCashLimitsState.setValue(newDefaultCashLimits);
    };
    const { validator, isValid } = useSingleValidator(true, useMemo(() => compositeValidator(nameValidator(t), wafValidator(t)), [t]));
    const initialValidity = !defaultCashLimitsState.value.every((defaultCashLimit) => defaultCashLimit.isValid);
    const [isDisabled, setIsDisabled] = useState(initialValidity);
    const initialValidation = initialValidity ? Promise.reject() : Promise.resolve();
    Promise.all([initialValidation, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const updateClearingHouseMutation = useExtendedMutation(UPDATE_CLEARING_HOUSE, {
        variables: {
            id: clearingHouse.id,
            code: clearingHouse.code,
            name: nameState.value,
            timeZone: timeZoneState.value,
            cashLimits: defaultCashLimitsState.value.map((defaultCashLimit) => ({
                currencyCode: defaultCashLimit.currencyCode,
                cashLimit: defaultCashLimit.cashLimit,
            })),
        },
        successCallback: () => setResetComponentsAfterModification(true),
        statesToResetOnSuccess: [nameState, timeZoneState, defaultCashLimitsState],
        successMessageOptions: { key: 'clearing-houses.notification.clearing-house-update-success', dataProperty: 'updateClearingHouse' },
        failureMessageOptions: { key: 'clearing-houses.notification.clearing-house-update-failure', params: { name: clearingHouse.name, code: clearingHouse.code } },
        refetchQueries: [ALL_CLEARING_HOUSES_QUERY_NAME],
    });
    const clearingHouseCanBeEdited = clearingHouse.modificationBlockers?.length === 0;
    const sufficientModificationPrivileges = clearingHouseCanBeEdited ? [Privileges.CLEARING_HOUSE_EDIT] : [];
    const deleteClearingHouseMutation = useExtendedMutation(DELETE_CLEARING_HOUSE, {
        variables: { id: clearingHouse.id },
        successCallback: onDetailDelete,
        successMessageOptions: { key: 'clearing-houses.notification.clearing-house-delete-success', dataProperty: 'deleteClearingHouse' },
        failureMessageOptions: { key: 'clearing-houses.notification.clearing-house-delete-failure', params: { name: clearingHouse.name, code: clearingHouse.code } },
        refetchQueries: [ALL_CLEARING_HOUSES_QUERY_NAME],
    });
    const isDeletionButtonDisabled = clearingHouse.deletionBlockers?.length > 0;
    return (
        <ExpandedListTab
            className="clearing-house-detail-tab"
            title={t('clearing-houses.clearing-house-detail')}
        >
            <div className="clearing-house-detail-tab--readonly-fields">
                <ReadonlyField
                    className="clearing-house-detail-tab--fields--code"
                    label={t('clearing-houses.code')}
                    value={clearingHouse.code}
                />
            </div>
            <div className="clearing-house-detail-tab--fields">
                <PrivilegedTextField
                    sufficientPrivileges={sufficientModificationPrivileges}
                    label={t('clearing-houses.name')}
                    state={nameState}
                    validator={validator}
                    className="clearing-house-detail-tab--fields--name"
                    inputId="clearing-house-detail-tab--fields--name"
                    maxLength={CLEARING_HOUSE_NAME_MAX_LETTERS}
                    placeholder={t('clearing-houses.placeholder.name')}
                    isRequired
                />
                <PrivilegedSelectField
                    sufficientPrivileges={sufficientModificationPrivileges}
                    label={t('clearing-houses.time-zone')}
                    state={timeZoneState}
                    className="clearing-house-detail-tab--fields--time-zone"
                    inputId="clearing-house-detail-tab--fields--time-zone"
                    options={availableTimeZones.map((availableTimeZone) => (
                        <option value={availableTimeZone.ID} key={availableTimeZone.ID}>{availableTimeZone.ID}</option>
                    ))}
                    isRequired
                />
            </div>
            <div className="clearing-house-detail-tab--zones">
                <ReadonlyList
                    className="clearing-house-detail-tab--fields--assigned-to-zones"
                    label={t('clearing-houses.assigned-to-zones')}
                    values={clearingHouse.zones.map((zone) => {
                        const zoneName = zone.name ? ` (${zone.name})` : '';
                        return `${zone.code}${zoneName}`;
                    })}
                />
            </div>
            <div className="clearing-house-detail-tab--fields--default-cash-limits">
                <DefaultCashLimitsTable
                    canBeEdited={clearingHouseCanBeEdited}
                    minCashLimit={minCashLimit}
                    maxCashLimit={maxCashLimit}
                    defaultCashLimits={defaultCashLimitsState.value}
                    onDefaultCashLimitChange={onDefaultCashLimitChange}
                    isChanged={defaultCashLimitsState.isChanged}
                    onReset={defaultCashLimitsState.reset}
                    doResetComponentsAfterModification={resetComponentsAfterModification}
                />
            </div>
            <Privileged sufficientPrivileges={[Privileges.CLEARING_HOUSE_EDIT]}>
                <div className="rich-list-row-expanded--buttons">
                    <Privileged sufficientPrivileges={sufficientModificationPrivileges}>
                        <div className="rich-list-row-expanded--buttons--left">
                            <ApplyChangesButton
                                disabled={isDisabled}
                                extendedMutation={updateClearingHouseMutation}
                                confirmationMessage={t('clearing-houses.confirm-update-clearing-house', { name: clearingHouse.name, code: clearingHouse.code })}
                            />
                        </div>
                    </Privileged>
                    <div className="rich-list-row-expanded--buttons--right">
                        <DeleteButton
                            extendedMutation={deleteClearingHouseMutation}
                            disabled={isDeletionButtonDisabled}
                            confirmationMessage={t('clearing-houses.confirm-delete-clearing-house', { name: clearingHouse.name, code: clearingHouse.code })}
                        />
                    </div>
                </div>
                {clearingHouse.modificationBlockers?.map((modificationBlockers) => (
                    <InfoPanel
                        key={modificationBlockers}
                        message={transformClearingHouseModificationBlockers(t, modificationBlockers, clearingHouse.name, clearingHouse.code)}
                    />
                ))}
                {clearingHouse.deletionBlockers?.map((deletionBlocker) => (
                    <InfoPanel
                        key={deletionBlocker}
                        message={transformClearingHouseBlockers(t, deletionBlocker, clearingHouse.name, clearingHouse.code)}
                    />
                ))}
            </Privileged>
        </ExpandedListTab>
    );
}

export default ClearingHouseDetailTab;
